import ValidateModule from "./ValidateModule";

import type {IEventChannel, ApiOpts} from "../model/Models";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type CacheModule from "./CacheModule";
import type { Criteria } from "../model/Criteria";
import type { SortOrder } from "../model/Feature";

const es = global.es;

///**
// * Defines the scale at which the symbol size will be increased to accommodate the PLUS
// */
//const SYMBOL_PLUS_SCALE = 4/3;

/**
 * @class SymbolTypeModule
 */
export default class SymbolTypeModule {
    private eventChannel: IEventChannel;
    private rqBroker: AjaxRequestBroker;
    private resourceName: string;
    private cache: CacheModule;

    //new SvgSymbolType(13, 0, 0, "routing_flag_start"...
    //new SvgSymbolType(14, 0, 0, "routing_flags_finish"...
    //new SvgSymbolType(15, 0, 0, "routing_flags_roundtrip"...
    //new SvgSymbolType(16, 0, 0, "routing_flags_waypoint"...
    readonly RouteSymbol = {
        START: 13,
        FINISH : 14,
        ROUNDTRIP : 15,
        WAYPOINT : 57,
        WAYPOINT_OLD : 16,
    };

    /**
     * 
     * @param eventChannel 
     * @param rqBroker 
     * @param resourceName 
     * @param cache 
     */
    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache?: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;

        // TODO FIXME Does this even make sense nowadays?
        eventChannel.addListener("onDestroy", () => {
            // prevent memory leaks when the map widget is destroyed
            this.eventChannel = this.rqBroker = this.resourceName = this.cache = null;
        });

        this.get = this.get.bind(this);
        this.getByIds = this.getByIds.bind(this);
        this.list = this.list.bind(this);
        this.listIds = this.listIds.bind(this);
        this.create = this.create.bind(this);
        this.update = this.update.bind(this);
        this.remove = this.remove.bind(this);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param id
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    get(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkInt(id, true)) {
            return es.util.deferred.rejected();
        }
        
        const url = es.api.getURL(this.resourceName) + "/" + id;

        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param ids
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    getByIds(ids: number[], opts?: ApiOpts) {
        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);

        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }
    
    /**
     * Get the resource from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    list(criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, opts?: ApiOpts) {
        //return rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }
    
    /**
     * Get the ids from the server
     * 
     * @param criteria
     * @param sortOrder
     * @param opts
     * 
     * @return a jquery deferred object resolved when the request has completed
     */
    listIds(criteria?: Criteria, sortOrder?: SortOrder, opts?: ApiOpts) {
        let url = [es.api.getURL(this.resourceName), "/listIds"];
        
        let delimiter = "?";
        if (criteria) {
            url.push(delimiter, "criteria=", es.util.url.stringify(criteria));
            delimiter = "&";
        }

        if (sortOrder) {
            url.push(delimiter, "sortOrder=", es.util.url.stringify(sortOrder));
        }

        return this.rqBroker.doGet({url: url.join(""), opts: opts});
    }
    
    /**
     * POST a new resource to the server (note: id attrib must be -1)
     * 
     * @param resourceObj
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    create(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        
        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName),
            opts: opts,
            data: resourceObj
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.eventChannel?.fireEvent("onCreateSymbolType", rs);
        });
    }
    
    /**
     * PUT a new resource to the server (note: id attrib must be valid)
     * 
     * @param resourceObj
     * @param opts
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    update(resourceObj: object, opts?: ApiOpts) {
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + resourceObj.id;
        
        return this.rqBroker.doPut({
            url: url,
            data: resourceObj,
            opts: opts
        }).done((rs) => {
            this.cache?.setEntity(this.resourceName, rs);
            this.cache?.clear("Style");
            this.eventChannel?.fireEvent("onCreateSymbolType", rs);
        });
    }
    
    /**
     * Removes the resource from the server
     * 
     * @param id 
     * @param opts 
     * 
     * @returns a jquery deferred object resolved when the request has completed
     */
    remove(id: number, opts?: ApiOpts) {
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;
        
        return this.rqBroker.doDelete({url: url, opts: opts}).done((rs) => {
            this.cache?.clear(this.resourceName, id);
            this.cache?.clear("Style");
            this.eventChannel?.fireEvent("onRemoveSymbolType", id);
        });
    }
}
